/**
 * My Accountant (katalog s. 15) — bağlan, izle, kopar.
 *
 * Bağlı ofis kartı ve "muhasebecinizin değiştirdikleri" my_accountant() ile;
 * kod girişi firm-link özelliğinin #fl-cust-box kutusunda, belge erişim
 * günlüğü file-access özelliğinin #fa-cust-log kutusunda (ikisi de bu sayfada).
 *
 * "What your accountant changed" akışı (katalog s. 16): kayıt eklemeleri ve
 * düzeltmeleri, belge talepleri (bekliyor/geldi, hatırlatma), kapatılan aylar;
 * "This week / All" süzgeci. Hepsi müşterinin kendi okuma yetkisiyle (RLS).
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';

interface Change { id: string; date: string; note: string | null; category: string | null; amount: number;
  currency: string | null; type: string; written_at: string; change: 'added' | 'edited'; by: string }
interface Info { ok: boolean; linked: boolean; link_id?: string; firm_name?: string; linked_at?: string;
  contacts?: string[]; changes?: Change[] }

let range: 'week' | 'all' = 'all';

function el(id: string): HTMLElement | null { return document.getElementById(id); }

interface FeedItem { at: string; letter: string; title: string; sub: string; tag: string; tagColor: string }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }

async function render(): Promise<void> {
  const root = el('ma-root');
  const sb = legacy.sb;
  if (!root || !sb) return;
  const r = await sb.rpc('my_accountant');
  const info = (r && r.data) as Info | null;
  (globalThis as any).flRender?.();
  (globalThis as any).faRenderLog?.();
  /* Bağlıyken kod kutusu gizlenir (katalog: bağlıyken kart, değilken bağlanma akışı). */
  const connect = el('fl-cust-box');
  if (connect) connect.style.display = info && info.linked ? 'none' : '';
  if (!info || !info.ok || !info.linked) { root.innerHTML = ''; return; }

  const since = info.linked_at ? new Date(info.linked_at).toLocaleDateString() : '';
  const contact = info.contacts && info.contacts.length ? ' · your contact: ' + info.contacts.map(esc).join(', ') : '';
  const firmLetter = (info.firm_name || '?').charAt(0).toUpperCase();
  const items: FeedItem[] = (info.changes || []).map((c) => ({
    at: c.written_at, letter: (c.by || '?').charAt(0).toUpperCase(),
    title: `${c.change === 'added' ? 'Added' : 'Corrected'} ${c.note || c.category || 'an entry'}`,
    sub: `${Number(c.amount).toFixed(2)} ${c.currency || ''} · ${c.by}`,
    tag: c.change === 'added' ? 'New' : 'Edit', tagColor: c.change === 'added' ? 'var(--grn)' : '#D4A017',
  }));
  const [reqs, periods] = await Promise.all([
    sb.from('document_requests').select('title,status,created_at,fulfilled_at,reminder_count,last_reminded_at')
      .order('created_at', { ascending: false }).limit(20),
    sb.from('accounting_periods').select('period,status,closed_at').eq('status', 'closed')
      .order('closed_at', { ascending: false }).limit(12),
  ]);
  for (const q of ((reqs && !reqs.error && reqs.data) || []) as any[]) {
    if (q.status === 'cancelled') continue;
    const reminded = q.reminder_count > 0 && q.last_reminded_at ? ` · reminder sent ${new Date(q.last_reminded_at).toLocaleDateString()}` : '';
    items.push({ at: q.created_at, letter: firmLetter, title: `Requested ${q.title}`,
      sub: q.status === 'fulfilled' && q.fulfilled_at ? `All received ${new Date(q.fulfilled_at).toLocaleDateString()}` : `Waiting for your upload${reminded}`,
      tag: q.status === 'fulfilled' ? 'Received' : 'Waiting', tagColor: q.status === 'fulfilled' ? 'var(--grn)' : '#ff9800' });
  }
  for (const pr of ((periods && !periods.error && periods.data) || []) as any[]) {
    if (!pr.closed_at) continue;
    const [y, m] = String(pr.period).split('-').map(Number);
    items.push({ at: pr.closed_at, letter: firmLetter,
      title: `Closed ${new Date(y, m - 1, 1).toLocaleDateString('en', { month: 'long', year: 'numeric' })}`,
      sub: 'Entries for this month are locked', tag: 'Period', tagColor: 'var(--t2)' });
  }
  const weekAgo = Date.now() - 7 * 864e5;
  const shown = items.filter((i) => range === 'all' || new Date(i.at).getTime() >= weekAgo)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  const changes = shown.map((i) =>
    `<div class="txi"><div class="av">${esc(i.letter)}</div>`
    + `<div class="tin"><div class="tnm">${esc(i.title)}</div>`
    + `<div class="tsb" style="color:var(--t2)"><span translate="no">${esc(i.sub)}</span> · <span translate="no">${esc(new Date(i.at).toLocaleString())}</span></div></div>`
    + `<div><span style="font-size:10px;color:${i.tagColor}">${esc(i.tag)}</span></div></div>`).join('');
  const pill = (v: 'week' | 'all', label: string) =>
    `<button class="fc${range === v ? ' on' : ''}" onclick="maRange('${v}')">${label}</button>`;

  root.innerHTML = `<div class="card" style="margin-bottom:12px;display:flex;align-items:center;gap:12px;flex-wrap:wrap">`
    + `<div class="av" style="width:40px;height:40px;font-size:14px">${esc((info.firm_name || '?').charAt(0))}</div>`
    + `<div style="flex:1;min-width:0"><div style="font-weight:700">${esc(info.firm_name)}</div>`
    + `<div style="font-size:11px;color:var(--t2)">Connected since <span translate="no">${esc(since)}</span>${contact}</div></div>`
    + `<span style="font-size:10px;font-weight:700;color:var(--grn)">● Active</span>`
    + `<button class="btn bg bsm" onclick="maDisconnect('${esc(info.link_id)}')">Disconnect</button></div>`
    + `<div class="card" style="margin-bottom:12px;padding:0;overflow:hidden"><div style="display:flex;align-items:center;justify-content:space-between;padding:14px 14px 0"><div class="set" style="margin:0">What your accountant changed</div><div style="display:flex;gap:6px">${pill('week', 'This week')}${pill('all', 'All')}</div></div><div style="height:10px"></div>`
    + (changes || `<div class="emp" style="padding:20px">${range === 'week' ? 'Nothing from your accountant this week.' : 'No changes yet. Every entry your accountant adds or corrects appears here with their name.'}</div>`)
    + `</div>`;
}

export default defineFeature({
  id: 'my-accountant',
  label: 'My Accountant (müşteri)',
  view: 'v10',
  mount: () => { void render(); },
  legacyGlobals: {
    maRange: (v: unknown) => { range = v === 'week' ? 'week' : 'all'; void render(); },
    maDisconnect: async (id: unknown) => {
      if (!confirm('Disconnect your accountant? Their access ends immediately; your data stays with you.')) return;
      const g = globalThis as any;
      if (typeof g.flRevoke === 'function') await g.flRevoke(String(id));
      void render();
      void g.navRefreshBadges?.();
    },
  },
});
